import { Row, Col } from 'react-bootstrap';
import { ExpenseType } from '../constants/expense-types';
import { ILineItem } from '../types/line-item.types';

interface DependentCareInfoCardProps {
    lineItems: ILineItem[];
}

interface DependentCareInfo {
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string | number;
    postalCode?: string;
}

export const DependentCareInfoCard = (props: DependentCareInfoCardProps) => {
    if (!props.lineItems || props.lineItems.length === 0) {
        return null;
    }

    const dependentCareItems = props.lineItems.filter(item => item.expenseType === ExpenseType.DEPENDENT_CARE);
    
    if (dependentCareItems.length === 0) {
        return null;
    }

    const parseDependentCareInfo = (description: string): DependentCareInfo | null => {
        if (!description || !description.trim()) {
            return null;
        }

        try {
            const parsed = JSON.parse(description.trim());
            if (parsed && typeof parsed === 'object') {
                return {
                    name: parsed.name || parsed.Name || null,
                    addressLine1: parsed.addressLine1 || parsed.AddressLine1 || parsed.address || null,
                    addressLine2: parsed.addressLine2 || parsed.AddressLine2 || null,
                    city: parsed.city || parsed.City || null,
                    state: parsed.state || parsed.State || null,
                    postalCode: parsed.postalCode || parsed.PostalCode || parsed.zipCode || parsed.zip || null
                };
            }
        } catch (e) {
            return { name: description.trim() };
        }
        return null;
    };

    const dependentCareInfos: DependentCareInfo[] = [];
    const seenDescriptions = new Set<string>();

    dependentCareItems.forEach(item => {
        if (item.description && item.description.trim()) {
            const desc = item.description.trim();
            if (!seenDescriptions.has(desc)) {
                seenDescriptions.add(desc);
                const parsed = parseDependentCareInfo(desc);
                if (parsed) {
                    dependentCareInfos.push(parsed);
                }
            }
        }
    });

    if (dependentCareInfos.length === 0) {
        return (
            <div style={{ 
                background: 'white', 
                borderRadius: '4px', 
                padding: '16px', 
                height: '100%',
                border: '1px solid #e1e8ed', 
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)' 
            }}>
                <div style={{ 
                    fontSize: '12px', 
                    fontWeight: 600, 
                    color: '#495057', 
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    Dependent Care Information
                </div>
                <div style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic' }}>
                    No dependent care name information available
                </div>
            </div>
        );
    }

    const formatCityStateZip = (info: DependentCareInfo) => {
        const parts: string[] = [];
        if (info.city) parts.push(info.city);
        if (info.state) parts.push(String(info.state));
        if (info.postalCode) parts.push(info.postalCode);
        return parts.length > 0 ? parts.join(', ') : null;
    };

    return (
        <div style={{ 
            background: 'white', 
            borderRadius: '4px', 
            padding: '16px', 
            height: '100%',
            border: '1px solid #e1e8ed', 
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)' 
        }}>
            <div style={{ 
                fontSize: '12px', 
                fontWeight: 600, 
                color: '#495057', 
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
            }}>
                Dependent Care Information
            </div>
            <div style={{ fontSize: '13px', color: '#212529' }}>
                {dependentCareInfos.map((info, index) => {
                    const hasAddress = info.addressLine1 || info.city || info.postalCode;
                    return (
                        <div key={index} style={{ marginBottom: index < dependentCareInfos.length - 1 ? '16px' : '0' }}>
                            <Row>
                                <Col md={6} xs={12}>
                                    {info.name && (
                                        <div>
                                            <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                                Name
                                            </div>
                                            <div style={{ fontWeight: 500 }}>
                                                {info.name}
                                            </div>
                                        </div>
                                    )}
                                </Col>
                                <Col md={6} xs={12}>
                                    {hasAddress && (
                                        <div>
                                            <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                                Address
                                            </div>
                                            <div style={{ lineHeight: '1.6' }}>
                                                {info.addressLine1 && <div>{info.addressLine1}</div>}
                                                {info.addressLine2 && <div>{info.addressLine2}</div>}
                                                {formatCityStateZip(info) && <div>{formatCityStateZip(info)}</div>}
                                            </div>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

